import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Users, ExternalLink, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { adminService } from '../services/admin';
import { useNavigate } from 'react-router-dom';

interface LocalUsersData {
  total: number;
  approved: number;
  pending: number;
  rejected: number;
}

const LocalUsers: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [localUsers, setLocalUsers] = useState<LocalUsersData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchLocalUsersData = async () => {
    try {
      setLoading(true);
      const users = await adminService.getLocalUsers();

      const data: LocalUsersData = {
        total: users.length,
        approved: users.filter(
          u => u.role !== 'user' || u.emailVerified !== false
        ).length,
        pending: users.filter(
          u => u.role === 'user' && u.emailVerified === false
        ).length,
        rejected: 0, // No tenemos campo de rejected en User, usando 0
      };

      setLocalUsers(data);
    } catch (error) {
      console.error('Error fetching local users data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'super-admin') {
      fetchLocalUsersData();
    }
  }, [user]);

  if (user?.role !== 'super-admin') {
    return (
      <div className='flex justify-center items-center py-16'>
        <Card>
          <CardContent className='text-center py-8'>
            <AlertTriangle className='w-12 h-12 text-yellow-500 mx-auto mb-4' />
            <h2 className='text-xl font-semibold mb-2'>Access Denied</h2>
            <p className='text-gray-600'>
              Only super administrators can access this section.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='space-y-4 sm:space-y-6'>
      <div className='flex justify-between items-center'>
        <div>
          <h1 className='text-2xl sm:text-3xl font-bold text-gray-900'>
            Team Setup
          </h1>
          <p className='text-sm sm:text-base text-gray-600 mt-1'>
            Management and statistics of local system users
          </p>
        </div>
      </div>

      {/* Local Users Overview */}
      <Card>
        <CardHeader>
          <CardTitle>
            <Users className='w-5 h-5 inline mr-2' />
            Users Summary
          </CardTitle>
          <CardDescription>
            Statistics and management of users registered in the system
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className='text-center py-8'>
              <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto'></div>
              <p className='text-gray-600 mt-2'>Loading user data...</p>
            </div>
          ) : localUsers ? (
            <div className='space-y-4'>
              <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                <div className='bg-blue-50 p-4 rounded-lg'>
                  <h3 className='text-lg font-semibold text-blue-700'>
                    {localUsers.total}
                  </h3>
                  <p className='text-sm text-blue-600'>Total</p>
                </div>
                <div className='bg-green-50 p-4 rounded-lg'>
                  <h3 className='text-lg font-semibold text-green-700'>
                    {localUsers.approved}
                  </h3>
                  <p className='text-sm text-green-600'>Approved</p>
                </div>
                <div className='bg-yellow-50 p-4 rounded-lg'>
                  <h3 className='text-lg font-semibold text-yellow-700'>
                    {localUsers.pending}
                  </h3>
                  <p className='text-sm text-yellow-600'>Pending</p>
                </div>
                <div className='bg-red-50 p-4 rounded-lg'>
                  <h3 className='text-lg font-semibold text-red-700'>
                    {localUsers.rejected}
                  </h3>
                  <p className='text-sm text-red-600'>Rejected</p>
                </div>
              </div>

              <div className='flex justify-end pt-4'>
                <Button
                  onClick={() => navigate('/admin/users')}
                  variant='outline'
                  className='flex items-center space-x-2'
                >
                  <span>Ver detalles completos</span>
                  <ExternalLink className='w-4 h-4' />
                </Button>
              </div>
            </div>
          ) : (
            <div className='text-center py-8'>
              <Users className='w-12 h-12 text-gray-400 mx-auto mb-4' />
              <p className='text-gray-600'>Could not load user data</p>
              <Button
                onClick={fetchLocalUsersData}
                variant='outline'
                className='mt-4'
              >
                Reintentar
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LocalUsers;
